import {
  ALLOWED_EMAIL_DOMAINS,
  MAX_NAME_LENGTH,
  MAX_TEXT_LENGTH,
  MIN_NAME_LENGTH,
  MIN_TEXT_LENGTH,
} from "@/consts";
import z from "zod";

// ==========================================
// Core Primitive Schemas
// ==========================================

export const DateSchema = z.coerce.date({
  errorMap: (issue, ctx) => {
    if (issue.code === z.ZodIssueCode.invalid_date) {
      return { message: "errors.validation.date.invalid" };
    }
    return { message: ctx.defaultError };
  },
});

export const EmailSchema = z
  .string({ required_error: "errors.validation.email.required" })
  .email({ message: "errors.validation.email.invalid" })
  .refine(
    (val) => {
      const domain = val.split("@")[1];
      return ALLOWED_EMAIL_DOMAINS.includes(domain);
    },
    { message: "errors.validation.email.domainNotAllowed" },
  );

export const IdSchema = z
  .string({
    required_error: "errors.validation.id.invalid",
    invalid_type_error: "errors.validation.id.invalid",
  })
  .uuid({
    message: "errors.validation.id.invalid",
  });

export const NameSchema = z
  .string({ required_error: "errors.validation.name.required" })
  .min(MIN_NAME_LENGTH, { message: "errors.validation.name.tooShort" })
  .max(MAX_NAME_LENGTH, { message: "errors.validation.name.tooLong" });

export const NoteSchema = z
  .string()
  .min(MIN_TEXT_LENGTH, { message: "errors.validation.note.tooShort" })
  .max(MAX_TEXT_LENGTH, { message: "errors.validation.note.tooLong" })
  .nullish();

export const KaratSchema = z.coerce
  .number()
  .min(0, "errors.validation.karat.invalid")
  .max(1000, "errors.validation.karat.invalid");

export const NonNegativeNumber = z.coerce.number().min(0);

export const PhoneSchema = z.string().regex(/^\+213[5-7][0-9]{8}$/, {
  message: "errors.validation.phone.invalidAlgerian",
});

export const IsActiveSchema = z.boolean({
  required_error: "errors.validation.isActive.required",
  invalid_type_error: "errors.validation.isActive.invalidType",
});

// ==========================================
// Domain Enums & Statuses
// ==========================================

export const MembershipStatusSchema = z.enum(["active", "expired", "cancelled"], {
  errorMap: () => ({ message: "errors.validation.membershipStatus.invalid" }),
});
export type MembershipStatus = z.infer<typeof MembershipStatusSchema>;

// ==========================================
// Data Table Filter Schemas
// ==========================================

export const TextFilterValueSchema = z.object({
  type: z.literal("text"),
  filterValue: z.string({ required_error: "errors.validation.filter.textRequired" }),
});

export const RadioFilterValueSchema = z.object({
  type: z.literal("radio"),
  filterValue: z.string({ required_error: "errors.validation.filter.radioRequired" }),
});

export const SelectFilterValueSchema = z.object({
  type: z.literal("select"),
  filterValue: z.array(z.string(), { required_error: "errors.validation.filter.selectRequired" }),
});

export const NumberRangeFilterValueSchema = z.object({
  type: z.literal("number"),
  filterValue: z.object({
    min: z.coerce
      .number({ invalid_type_error: "errors.validation.filter.minMustBeNumber" })
      .optional(),
    max: z.coerce
      .number({ invalid_type_error: "errors.validation.filter.maxMustBeNumber" })
      .optional(),
  }),
}).refine(
  (data) => {
    if (data.filterValue.min != null && data.filterValue.max != null) {
      return data.filterValue.min <= data.filterValue.max;
    }
    return true;
  },
  { message: "errors.validation.filter.minGreaterThanMax" },
);

export const DateRangeFilterValueSchema = z.object({
  type: z.literal("date"),
  filterValue: z.object({
    from: z.coerce
      .date({ invalid_type_error: "errors.validation.filter.invalidFromDate" })
      .optional(),
    to: z.coerce.date({ invalid_type_error: "errors.validation.filter.invalidToDate" }).optional(),
  }),
});

export const ColumnFilterValueSchema = z.union(
  [
    TextFilterValueSchema,
    RadioFilterValueSchema,
    SelectFilterValueSchema,
    NumberRangeFilterValueSchema,
    DateRangeFilterValueSchema,
  ],
  {
    errorMap: () => ({ message: "errors.validation.filter.invalidFilterType" }),
  },
);

export type TextFilterValue = z.infer<typeof TextFilterValueSchema>;
export type RadioFilterValue = z.infer<typeof RadioFilterValueSchema>;
export type SelectFilterValue = z.infer<typeof SelectFilterValueSchema>;
export type NumberRangeFilterValue = z.infer<typeof NumberRangeFilterValueSchema>;
export type DateRangeFilterValue = z.infer<typeof DateRangeFilterValueSchema>;
export type ColumnFilterValue = z.infer<typeof ColumnFilterValueSchema>;

export const cursorPaginatedResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    items: z.array(itemSchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
  });
